import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store';
import { ClientType } from '~/enums';
import { areSameCompanies } from '~/helpers/utils';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import {
  resetCompanyInForm,
  setClientId,
  setCompanyIsNew,
  updateApplicantData,
  updateForm,
} from '~/store/slices/loanApplication.slice';
import { ILoanApplicationClient } from '~/interfaces/loanApplication';
import { Badge, Group, Radio } from '@qred/components-library';

interface CompanySelectorRadioButtonsProps {
  clients: ILoanApplicationClient[];
}

const CompanySelectorRadioButtons = ({
  clients,
}: CompanySelectorRadioButtonsProps) => {
  const t = useTranslate();
  const dispatch = useAppDispatch();

  const {
    form,
    companyIsNew,
    currentApplicationOrganizationIdentifier,
  } = useSelector((state: RootState) => state.loanApplication);

  const updateOrganizationInRedux = (org: ILoanApplicationClient) => {
    const isRLC = org.active;

    dispatch(
      updateForm({
        organizationNumber: org.company_no ?? undefined,
        companyType: org.company_type ?? undefined,
        clientType: isRLC ? ClientType.RLC : ClientType.NLC,
        companyName: org.company_name ?? undefined,
        connectId: org.connect_id ?? undefined,
        creditReportProvider:
          org.credit_report_provider_number && org.credit_report_provider_name
            ? {
                creditReportProviderNumber: org.credit_report_provider_number,
                creditReportProviderName: org.credit_report_provider_name,
              }
            : undefined,
        companyTypeCategory: org.company_type_category ?? undefined,
        vatNumber: org.vat_number ?? undefined,
        companyAddress: {
          street: org.street ?? '',
          city: org.city ?? '',
          zipCode: org.zip_code ?? '',
        },
      })
    );

    dispatch(
      updateApplicantData({
        email: isRLC && org.email ? org.email : '',
        phone: isRLC && org.phone ? org.phone : '',
      })
    );
  };

  useEffect(() => {
    if (
      clients.length &&
      !form.organizationNumber &&
      !form.creditReportProvider
    ) {
      updateOrganizationInRedux(clients[0]);
    }
  }, [clients]);

  useEffect(() => {
    if (
      !areSameCompanies(
        form.organizationNumber ||
          form.creditReportProvider?.creditReportProviderNumber,
        currentApplicationOrganizationIdentifier
      )
    ) {
      dispatch(updateForm({ companyName: '' }));
    }
  }, [companyIsNew]);

  const onChange = (
    company: Pick<
      ILoanApplicationClient,
      | 'company_no'
      | 'credit_report_provider_number'
      | 'credit_report_provider_name'
    >
  ) => {
    if (companyIsNew) {
      dispatch(setCompanyIsNew(false));
    }

    if (
      areSameCompanies(
        form.organizationNumber ||
          form.creditReportProvider?.creditReportProviderNumber,
        company.company_no || company.credit_report_provider_number
      )
    ) {
      return;
    }

    const selectedClient = clients.find((client) =>
      areSameCompanies(
        client.company_no || client.credit_report_provider_number,
        company.company_no || company?.credit_report_provider_number
      )
    );

    if (selectedClient) {
      updateOrganizationInRedux(selectedClient);
      if (selectedClient.client_id) {
        dispatch(setClientId(selectedClient.client_id));
      }
      dispatch(
        pushToGtmLoanAction({
          stepActionNumber: 0.1,
          actionName: 'company_change',
        })
      );
    }
  };

  const handleNewCompany = () => {
    dispatch(setCompanyIsNew(true));
    dispatch(resetCompanyInForm());
    dispatch(
      updateApplicantData({
        email: '',
        phone: '',
      })
    );
    dispatch(
      pushToGtmLoanAction({
        stepActionNumber: 0.1,
        actionName: 'company_change',
      })
    );
  };

  return (
    <Radio.Group
      direction="column"
      dataCy="company_selector_checkbox"
      value={
        companyIsNew
          ? 'company_new'
          : form.organizationNumber ||
            form.creditReportProvider?.creditReportProviderNumber
      }
    >
      {clients.map((company: ILoanApplicationClient) => (
        <Radio
          key={company.company_no || company.credit_report_provider_number}
          value={
            company.company_no ||
            company.credit_report_provider_number ||
            undefined
          }
          onClick={() => onChange(company)}
          label={
            <Group>
              {company.company_name}
              {company.application_uuid &&
                (company.has_inprogress_application ||
                  company.has_offer_to_sign) && (
                  <Badge
                    color={
                      company.has_offer_to_sign ? 'success.500' : 'warning.500'
                    }
                  >
                    {company.has_offer_to_sign
                      ? t('Onboarding.ActiveOffer')
                      : t('Onboarding.ActiveApplication')}
                  </Badge>
                )}
            </Group>
          }
          hint={company.company_no}
        />
      ))}
      <Radio
        value="company_new"
        onClick={handleNewCompany}
        dataCy="company_selector_checkbox_new_company"
        label={t('Onboarding.NewCompany')}
      />
    </Radio.Group>
  );
};

export default CompanySelectorRadioButtons;
